import { useEffect, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';

import { Button } from '@nesso/ui/primitives/button';
import { InputField } from '@nesso/ui/primitives/input-field';
import { MaskedInputField, US_PHONE_MASK } from '@nesso/ui/primitives/masked-input-field';

import {
    type AddClientInput,
    addClientInputSchema,
    type EmailInUseError,
    MAX_INPUT_FIELD_LENGTH,
    resolveEmailInUseError,
    type SelectedClient,
    useAddClientPracticeMemberOptions,
    useCreateClientOptions,
} from '@/entities/client';
import { useSheetStore } from '@/shared/stores/sheet-store';
import { FieldReveal } from '@/shared/ui/forms/field-reveal';
import { PronounsFields } from '@/shared/ui/forms/pronouns-fields';
import { SheetFooter } from '@/shared/ui/panels/sheet-panel';

import { hasAddClientEntries } from '../../lib/has-add-client-entries';
import { AddClientFormEmailInUse } from './add-client-form/add-client-form-email-in-use';

type Props = {
    firstName: string;
    lastName: string;
    onCreated?: (client: SelectedClient) => void;
    onCancel: () => void;
    onUnsavedEntriesChange: (hasUnsavedEntries: boolean) => void;
};

export function AddClientForm({
    firstName,
    lastName,
    onCreated,
    onCancel,
    onUnsavedEntriesChange,
}: Props) {
    const replaceSheets = useSheetStore((state) => state.replaceSheets);
    const createClientMutation = useMutation(useCreateClientOptions());
    const addPracticeMemberMutation = useMutation(useAddClientPracticeMemberOptions());

    const [emailInUse, setEmailInUse] = useState<EmailInUseError | null>(null);

    const form = useForm<AddClientInput>({
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        resolver: zodResolver(addClientInputSchema, undefined, { raw: true }),
        defaultValues: {
            firstName,
            lastName,
            preferredFirstName: '',
            emailPrimary: '',
            phonePrimary: '',
            pronouns: null,
        },
    });
    const {
        control,
        formState: { isSubmitting },
    } = form;

    // Entries already on their way to the server are not unsaved, so a pending create never asks.
    const values = useWatch({ control });
    const hasUnsavedEntries = !isSubmitting && hasAddClientEntries(values);
    // The cleanup disarms the shell's discard guard when this form unmounts, so a form that is gone
    // cannot leave it armed for the next one.
    useEffect(() => {
        onUnsavedEntriesChange(hasUnsavedEntries);
        return () => onUnsavedEntriesChange(false);
    }, [hasUnsavedEntries, onUnsavedEntriesChange]);

    // A create that resolves after this form is gone must not act on the sheet a later open owns.
    const isMountedRef = useRef(true);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const openExistingClient = async (
        holder: Extract<EmailInUseError['holder'], { kind: 'CLIENT' }>,
    ) => {
        if (!holder.ownedByYou) {
            try {
                await addPracticeMemberMutation.mutateAsync(holder.clientId);
            } catch {
                // The mutation's own toast reports it, and the link stays so the therapist can retry.
                return;
            }
            if (!isMountedRef.current) return;
        }
        if (onCreated) {
            onCreated({ id: holder.clientId, name: holder.name });
            replaceSheets((sheet) => (sheet.type === 'clients/add' ? null : sheet));
            return;
        }
        replaceSheets((sheet) =>
            sheet.type === 'clients/add'
                ? {
                      type: 'clients/details',
                      label: holder.name,
                      data: { id: holder.clientId, name: holder.name },
                  }
                : sheet,
        );
    };

    const onSubmit = async (data: AddClientInput) => {
        try {
            const { id, name } = await createClientMutation.mutateAsync(data);
            if (!isMountedRef.current) return;
            if (onCreated) {
                onCreated({ id, name });
                replaceSheets((sheet) => (sheet.type === 'clients/add' ? null : sheet));
                return;
            }
            replaceSheets((sheet) =>
                sheet.type === 'clients/add'
                    ? { type: 'clients/details', label: name, data: { id, name } }
                    : sheet,
            );
        } catch (error) {
            // Anything but an email-in-use refusal surfaces through the mutation's own toast. Either
            // way the form keeps what the therapist entered.
            if (!isMountedRef.current) return;
            setEmailInUse(resolveEmailInUseError(error));
        }
    };

    return (
        <FormProvider {...form}>
            <form
                noValidate
                onSubmit={form.handleSubmit(onSubmit)}
                className="flex min-h-0 flex-1 flex-col"
            >
                <div className="flex flex-1 flex-col gap-8 overflow-y-auto px-4 pt-6 pb-8">
                    <h2 className="text-xl font-bold">Client</h2>
                    <div className="grid gap-4 md:grid-cols-2">
                        <Controller
                            control={control}
                            name="firstName"
                            render={({ field, fieldState }) => (
                                <InputField
                                    {...field}
                                    id={field.name}
                                    label="First name"
                                    disabled={isSubmitting}
                                    maxLength={MAX_INPUT_FIELD_LENGTH}
                                    error={fieldState.error?.message ?? fieldState.invalid}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="lastName"
                            render={({ field, fieldState }) => (
                                <InputField
                                    {...field}
                                    id={field.name}
                                    label="Last name"
                                    disabled={isSubmitting}
                                    maxLength={MAX_INPUT_FIELD_LENGTH}
                                    error={fieldState.error?.message ?? fieldState.invalid}
                                />
                            )}
                        />
                    </div>
                    <FieldReveal label="Add preferred first name" disabled={isSubmitting}>
                        <div className="grid gap-4 md:grid-cols-2">
                            <Controller
                                control={control}
                                name="preferredFirstName"
                                render={({ field, fieldState }) => (
                                    <InputField
                                        {...field}
                                        value={field.value ?? ''}
                                        id={field.name}
                                        label="Preferred first name (optional)"
                                        disabled={isSubmitting}
                                        maxLength={MAX_INPUT_FIELD_LENGTH}
                                        error={fieldState.error?.message ?? fieldState.invalid}
                                    />
                                )}
                            />
                        </div>
                    </FieldReveal>
                    <Controller
                        control={control}
                        name="emailPrimary"
                        render={({ field, fieldState }) => (
                            <InputField
                                {...field}
                                onChange={(event) => {
                                    field.onChange(event);
                                    setEmailInUse(null);
                                }}
                                value={field.value ?? ''}
                                id={field.name}
                                type="email"
                                label="Email (optional)"
                                disabled={isSubmitting}
                                maxLength={MAX_INPUT_FIELD_LENGTH}
                                error={
                                    emailInUse ? (
                                        <AddClientFormEmailInUse
                                            holder={emailInUse.holder}
                                            isPending={addPracticeMemberMutation.isPending}
                                            onUseExisting={openExistingClient}
                                        />
                                    ) : (
                                        (fieldState.error?.message ?? fieldState.invalid)
                                    )
                                }
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="phonePrimary"
                        render={({ field, fieldState }) => (
                            <MaskedInputField
                                {...field}
                                value={field.value ?? ''}
                                id={field.name}
                                label="Phone number (optional)"
                                placeholder="(   )    -    "
                                mask={US_PHONE_MASK}
                                disabled={isSubmitting}
                                error={fieldState.error?.message ?? fieldState.invalid}
                                labelClassName="whitespace-nowrap"
                                fieldClassName="max-w-37.75"
                            />
                        )}
                    />
                    <FieldReveal label="Add pronouns" disabled={isSubmitting}>
                        <PronounsFields
                            label="Pronouns (optional)"
                            maxLength={MAX_INPUT_FIELD_LENGTH}
                            optionsClassName="w-33.75 grid-cols-1"
                        />
                    </FieldReveal>
                </div>
                <SheetFooter className="border-t-0">
                    <Button type="button" variant="ghost" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                        Create Client
                    </Button>
                </SheetFooter>
            </form>
        </FormProvider>
    );
}
